import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AvatarDisplay from '../components/AvatarDisplay';
import {
  setUserSuspendedApi, deleteUserApi, moveDroneApi, getAdminDronesApi, getAdminMissionsApi,
  getAdminStatsApi, getAdminUsersApi,
  getAdminAirspaceApi, releaseIntentApi, getAdminAuditApi,
  getAdminDeliveriesApi, getHealthApi, getAdminMonitorApi,
  inviteUserApi, updateUserRoleApi,
  sendUserEmailApi,
} from '../services/authApi';
import {
  attentionItems, auditDay, auditDetail, auditMatches, auditTarget, DELIVERY_STATUS_ORDER, deliveryStatusText, droneLink,
  droneOnline, fmtLatency, memberMatches, memberSummary, moveTargets, operatorsOf, releaseRisk, reservationClock, sortDeliveries, sortFleet,
  sortMembers, sortReservations,
} from '../utils/adminConsole';
import './AdminPage.css';

/* ─── Vocabulary ─── */
const INTENT_STATES = [
  { key: 'Accepted', label: 'Reserved', note: 'Cleared, not airborne yet', color: 'var(--a-blue)' },
  { key: 'Activated', label: 'Flying', note: 'Airborne inside its volume', color: 'var(--a-green)' },
  { key: 'Nonconforming', label: 'Off route', note: 'Outside its volume, recoverable', color: 'var(--a-amber)' },
  { key: 'Contingent', label: 'Contingency', note: 'Link loss, low battery or abort', color: 'var(--a-red)' },
  { key: 'Ended', label: 'Closed today', note: 'Released in the last 24 hours', color: 'var(--a-mute)' },
];
const STATE_BY_KEY = Object.fromEntries(INTENT_STATES.map(s => [s.key, s]));

const DELIVERY_COLOR = {
  requested: 'var(--a-amber)', pending: 'var(--a-blue)', assigned: 'var(--a-cyan)',
  in_flight: 'var(--a-green)', landed: 'var(--a-green)', delivered: 'var(--a-soft)', failed: 'var(--a-red)', cancelled: 'var(--a-mute)',
};

const AUDIT_TEXT = {
  'drone.connect': 'Connected a drone',
  'drone.create': 'Registered a drone',
  'drone.update': 'Edited a drone',
  'drone.delete': 'Removed a drone',
  'drone.move': 'Moved a drone',
  'drone.force_disarm': 'Force-disarmed a drone',
  'drone.calibrate_started': 'Started calibration',
  'drone.frame_configured': 'Changed frame setup',
  'drone.device_token.issue': 'Issued a device token',
  'mission.create': 'Saved a mission',
  'mission.start': 'Launched a mission',
  'mission.start.blocked': 'Launch blocked by airspace conflict',
  'mission.start.conflict_override': 'Overrode an airspace conflict',
  'mission.delete_all': 'Deleted every mission',
  'config.update': 'Changed system settings',
  'user.delete': 'Removed a member',
  'user.invite': 'Added a member',
  'user.role_change': 'Changed a role',
  'user.reset_link': 'Emailed a password reset link',
  'user.invite_sent': 'Re-sent an invitation',
  'user.password_reset': 'Set password from a link',
  'user.password_change': 'Changed own password',
  'user.forgot_password_request': 'Asked for a password reset',
  'user.suspend': 'Suspended a member',
  'user.reactivate': 'Reactivated a member',
  'airspace.release': 'Released a reservation',
};
const auditColor = (a) => (
  /force_disarm|delete|override/.test(a) ? 'var(--a-red)'
    : /blocked|release/.test(a) ? 'var(--a-amber)'
      : /start|connect/.test(a) ? 'var(--a-green)' : 'var(--a-cyan)'
);

function ago(t) {
  const s = Math.max(0, (Date.now() - new Date(t).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
const hhmm = (t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
// Recent joins read better as an age, old ones as a date.
const joinedText = (t) => ((Date.now() - new Date(t).getTime()) / 86400000 < 30
  ? ago(t)
  : new Date(t).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }));
const fmtDist = (m) => (m >= 1000 ? `${+(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);
function fmtUptime(sec) {
  if (sec == null) return '—';
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}

function Icon({ d, size = 16 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;
}
const I = {
  command: 'M12 2a10 10 0 100 20 10 10 0 000-20zm0 4a6 6 0 100 12 6 6 0 000-12zm0 4a2 2 0 100 4 2 2 0 000-4z',
  airspace: 'M3 12h4l3-8 4 16 3-8h4',
  members: 'M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
  fleet: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
  missions: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  deliveries: 'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16zM3.3 7L12 12l8.7-5M12 22V12',
  activity: 'M12 8v4l3 3M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  system: 'M4 6h16M4 12h16M4 18h16M8 6v0M8 12v0M8 18v0',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  lock: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  refresh: 'M21 12a9 9 0 11-2.64-6.36M21 3v6h-6',
  mail: 'M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7zm0 1l9 6 9-6',
};

/* ─── Radar scope: live reservations + positioned drones ─── */
const NICE_RANGES = [250, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 250000, 500000, 1000000];

const shortName = (name) => (name.length > 18 ? `${name.slice(0, 17)}…` : name);

function Scope({ intents = [], drones = [], selected = null }) {
  const geo = useMemo(() => {
    const pts = [...drones.map(d => [d.lat, d.lon]), ...intents.flatMap(i => i.route)];
    if (!pts.length) return null;
    const lat0 = pts.reduce((s, p) => s + p[0], 0) / pts.length;
    const lon0 = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    const k = Math.cos((lat0 * Math.PI) / 180);
    const toM = ([lat, lon]) => [(lon - lon0) * 111320 * k, (lat - lat0) * 110540];
    // ponytail: range fits every point, so one far-away drone shrinks the rest; add zoom if fleets spread across cities
    const maxR = Math.max(150, ...pts.map(p => Math.hypot(...toM(p))));
    const range = NICE_RANGES.find(n => n >= maxR * 1.1) || NICE_RANGES[NICE_RANGES.length - 1];
    const s = 100 / range;
    return { range, xy: (p) => { const [x, y] = toM(p); return [x * s, -y * s]; } };
  }, [intents, drones]);

  return (
    <div className="adm-scope">
      <svg viewBox="-112 -112 224 224" role="img" aria-label={`Airspace scope: ${intents.length} reservations, ${drones.length} drones with position`}>
        <defs>
          <radialGradient id="adm-scope-bg">
            <stop offset="0" stopColor="var(--a-cyan)" stopOpacity=".07" />
            <stop offset="1" stopColor="var(--a-cyan)" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle r="100" fill="url(#adm-scope-bg)" />
        {[25, 50, 75, 100].map(r => <circle key={r} className="ring" r={r} />)}
        <line className="axis" x1="-100" y1="0" x2="100" y2="0" />
        <line className="axis" x1="0" y1="-100" x2="0" y2="100" />
        {[30, 60, 120, 150].map(a => {
          const t = (a * Math.PI) / 180;
          return <line key={a} className="axis" x1={Math.cos(t) * 100} y1={Math.sin(t) * 100} x2={-Math.cos(t) * 100} y2={-Math.sin(t) * 100} opacity=".5" />;
        })}
        <text className="ring-label" x="3" y="-101">N</text>
        {geo && [1, 2, 3, 4].map(q => (
          <text key={q} className="ring-label" x={q * 25 * 0.707 + 2} y={-q * 25 * 0.707 - 1}>{fmtDist((geo.range * q) / 4)}</text>
        ))}

        <g className="sweep">
          {[[0, 0.1], [14, 0.05], [28, 0.025]].map(([off, op]) => {
            const a0 = ((-90 - off) * Math.PI) / 180, a1 = ((-90 - off - 14) * Math.PI) / 180;
            return <path key={off} d={`M0 0 L${Math.cos(a0) * 100} ${Math.sin(a0) * 100} A100 100 0 0 0 ${Math.cos(a1) * 100} ${Math.sin(a1) * 100} Z`} fill="var(--a-cyan)" opacity={op} />;
          })}
          <line x1="0" y1="0" x2="0" y2="-100" stroke="var(--a-cyan)" strokeWidth=".5" opacity=".35" />
        </g>

        {geo && intents.map(i => {
          const st = STATE_BY_KEY[i.state] || STATE_BY_KEY.Accepted;
          const pts = i.route.map(geo.xy);
          if (!pts.length) return null;
          const picked = selected?.intentId === i.intentId;
          return (
            <g key={i.intentId} opacity={selected && !picked ? 0.15 : 1}>
              <polyline className="route" points={pts.map(p => p.join(',')).join(' ')} stroke={st.color}
                strokeDasharray={i.state === 'Accepted' ? '3 2' : undefined} strokeWidth={picked ? 2.4 : i.priority ? 1.8 : 1.1} opacity=".9" />
              {pts.map((p, n) => <circle key={n} className="route-pt" cx={p[0]} cy={p[1]} r=".9" fill={st.color} />)}
            </g>
          );
        })}

        {geo && drones.map(d => {
          const [x, y] = geo.xy([d.lat, d.lon]);
          const color = d.armed ? 'var(--a-green)' : d.status === 'connected' ? 'var(--a-cyan)' : 'var(--a-mute)';
          const h = ((d.heading || 0) * Math.PI) / 180;
          return (
            <g key={d.droneId} opacity={selected && selected.droneId !== d.droneId ? 0.15 : 1}>
              {d.armed && <circle className="ping" cx={x} cy={y} r="2.5" fill="none" stroke={color} strokeWidth=".6" />}
              <line x1={x} y1={y} x2={x + Math.sin(h) * 6} y2={y - Math.cos(h) * 6} stroke={color} strokeWidth=".7" />
              <circle cx={x} cy={y} r="2.2" fill={color} />
              <text className="blip-label" x={x > 45 ? x - 4 : x + 4} y={y + 1.8} textAnchor={x > 45 ? 'end' : 'start'}>{shortName(d.name || d.droneId)}</text>
            </g>
          );
        })}
      </svg>
      {!geo && <div className="adm-scope-empty">Airspace is clear. Drones and reservations show up here once they report a position.</div>}
    </div>
  );
}

/* ─── Small parts ─── */
function Meter({ label, value, max, color }) {
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="adm-meter">
      <div className="adm-meter-top"><span>{label}</span><span className="num">{value} of {max ?? '∞'}</span></div>
      <div className="adm-meter-bar"><div style={{ width: `${pct}%`, background: color }} /></div>
    </div>
  );
}

function Tag({ color, children }) {
  return <span className="adm-tag" style={{ color }}>{children}</span>;
}

/* API calls / errors / latency since the backend last started (in-memory, resets on deploy). */
function ApiHealth({ monitor }) {
  const errRate = monitor.total ? (monitor.c5xx / monitor.total) * 100 : 0;
  const rateColor = errRate >= 5 ? 'var(--a-red)' : errRate >= 1 ? 'var(--a-amber)' : 'var(--a-green)';
  const shown = monitor.routes.slice(0, 25);
  return (
    <>
      <div className="adm-panel adm-wide">
        <h3 className="adm-h3">API health <small>since {ago(monitor.since)}, resets when the backend restarts</small></h3>
        <div className="adm-figs">
          <div><span className="num">{monitor.total.toLocaleString()}</span><small>Requests</small></div>
          <div><span className="num" style={{ color: rateColor }}>{errRate.toFixed(1)}%</span><small>Server errors ({monitor.c5xx})</small></div>
          <div><span className="num">{monitor.c4xx}</span><small>Refused requests (4xx)</small></div>
        </div>
      </div>
      <div className="adm-panel flush adm-wide">
        <h3 className="adm-h3 adm-panel-title">Recent errors and slow calls</h3>
        {monitor.recentErrors.length === 0 ? <p className="adm-empty">None since the last restart.</p> : (
          <div className="adm-scroll">
            <table className="adm-table">
              <thead><tr><th>When</th><th>What</th><th className="hide-mob">User</th><th>Message</th></tr></thead>
              <tbody>
                {monitor.recentErrors.map((e, i) => (
                  <tr key={i}>
                    <td className="nowrap" title={new Date(e.at).toLocaleString()}>{hhmm(e.at)}</td>
                    <td>
                      <Tag color={e.kind === 'slow' ? 'var(--a-amber)' : 'var(--a-red)'}>{e.kind === 'slow' ? `slow ${fmtLatency(e.ms)}` : (e.status || e.kind)}</Tag>
                      <small className="adm-line id">{e.path || e.kind}</small>
                    </td>
                    <td className="hide-mob">{e.user || '—'}</td>
                    <td className="ell" title={e.message || undefined}>{e.message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="adm-panel flush adm-wide">
        <h3 className="adm-h3 adm-panel-title">Routes <small>failing first{monitor.routes.length > shown.length ? `, top ${shown.length} of ${monitor.routes.length}` : ''}</small></h3>
        <div className="adm-scroll">
          <table className="adm-table">
            <thead><tr><th>Route</th><th className="r">Calls</th><th className="r">5xx</th><th className="r hide-mob">4xx</th><th className="r">p95</th><th className="hide-mob">Last call</th></tr></thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={6} className="adm-empty">No requests yet.</td></tr>}
              {shown.map(r => (
                <tr key={r.route}>
                  <td className="ell wide" title={r.route}><span className="id">{r.route}</span></td>
                  <td className="r num">{r.count.toLocaleString()}</td>
                  <td className="r num" style={{ color: r.c5xx ? 'var(--a-red)' : 'var(--a-mute)' }}>{r.c5xx}</td>
                  <td className="r num hide-mob" style={{ color: r.c4xx ? undefined : 'var(--a-mute)' }}>{r.c4xx}</td>
                  <td className="r num nowrap" style={{ color: r.p95Ms > 2000 ? 'var(--a-amber)' : undefined }}>{fmtLatency(r.p95Ms)}</td>
                  <td className="hide-mob nowrap">{ago(r.lastAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Seg({ value, options, onChange, label }) {
  return (
    <div className="adm-seg" role="group" aria-label={label}>
      {options.map(([k, text]) => (
        <button key={k} type="button" aria-pressed={value === k} onClick={() => onChange(k)}>{text}</button>
      ))}
    </div>
  );
}

function OperatorFilter({ list, value, onChange }) {
  const operators = operatorsOf(list);
  if (operators.length < 2) return null;
  return (
    <select className="adm-search" value={value} onChange={e => onChange(e.target.value)} aria-label="Filter by operator">
      <option value="">All operators</option>
      {operators.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function AuditFeed({ logs, nameOf }) {
  if (!logs.length) return <div className="adm-empty">Nothing recorded yet. Launches, connections and admin actions land here.</div>;
  return (
    <ul className="adm-feed">
      {logs.map(l => (
        <li key={l.id}>
          <i style={{ background: auditColor(l.action) }} />
          <div style={{ minWidth: 0 }}>
            <div className="what">{AUDIT_TEXT[l.action] || l.action}{auditTarget(l, nameOf) ? <span className="id"> {auditTarget(l, nameOf)}</span> : null}</div>
            <div className="who">{[l.user?.name || 'System', auditDetail(l.detail)].filter(Boolean).join(' · ')}</div>
          </div>
          <time dateTime={l.at} title={new Date(l.at).toLocaleString()}>{ago(l.at)}</time>
        </li>
      ))}
    </ul>
  );
}

/* ─── Page ─── */
const TAB_KEY = 'utms_admin_tab';
const savedTab = () => {
  try { return sessionStorage.getItem(TAB_KEY) || 'command'; } catch { return 'command'; }
};
// These change on their own, so they refresh on their own; the rest are snapshots.
const LIVE_TABS = new Set(['command', 'airspace', 'fleet', 'system']);
const STALE_AFTER_MS = 30000;
const AUDIT_LIMIT = 300;
const answered = (r) => {
  if (!r || !r.success) throw new Error((r && r.message) || 'The server refused the request');
  return r;
};

const NAV = [
  { group: 'Airspace' },
  { key: 'command', label: 'Command', icon: I.command },
  { key: 'airspace', label: 'Reservations', icon: I.airspace },
  { group: 'Operations' },
  { key: 'fleet', label: 'Fleet', icon: I.fleet },
  { key: 'missions', label: 'Missions', icon: I.missions },
  { key: 'deliveries', label: 'Deliveries', icon: I.deliveries },
  { group: 'Governance' },
  { key: 'members', label: 'Members', icon: I.members },
  { key: 'activity', label: 'Audit trail', icon: I.activity },
  { key: 'system', label: 'System', icon: I.system },
];

function AdminPage({ isMobileLayout = false, dronesTelemetry = {} }) {
  const { user: currentUser } = useAuth();
  const mob = isMobileLayout;
  const isAdmin = currentUser?.role === 'admin';
  const myId = currentUser?._id || currentUser?.id;

  const [tab, setTab] = useState(savedTab);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [airspace, setAirspace] = useState({ intents: [], drones: [] });
  const [users, setUsers] = useState([]);
  const [drones, setDrones] = useState([]);
  const [missions, setMissions] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [logs, setLogs] = useState([]);
  const [monitor, setMonitor] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'operator' });
  const [adding, setAdding] = useState(false);
  const addingRef = useRef(false); // state alone lets a fast double click through before it re-renders
  const [sendingTo, setSendingTo] = useState(null); // member id whose email is on its way
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState({}); // section → it has real data (until then "no members" would be a lie)
  const [lastOk, setLastOk] = useState(null);
  const [msg, setMsg] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [pickedIntent, setPickedIntent] = useState(null);
  const [now, setNow] = useState(Date.now());

  const [q, setQ] = useState('');
  const [who, setWho] = useState('');
  const [filter, setFilter] = useState('all');

  const isDroneOnline = useCallback((d) => droneOnline(d, dronesTelemetry || {}), [dronesTelemetry]);

  const load = useCallback(async (key, quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const jobs = [
        getAdminStatsApi().then(answered).then(r => setStats(r.stats)),
        getHealthApi().then(setHealth).catch(() => setHealth({ status: 'error', services: {} })),
      ];
      const airspaceJob = () => getAdminAirspaceApi().then(answered).then(setAirspace);
      const dronesJob = () => getAdminDronesApi().then(answered).then(r => setDrones(r.drones));
      if (key === 'command') {
        jobs.push(airspaceJob(), dronesJob());
        jobs.push(getAdminAuditApi(6).then(answered).then(r => setLogs(r.logs)));
      }
      if (key === 'airspace') jobs.push(airspaceJob(), dronesJob());
      if (key === 'members') jobs.push(getAdminUsersApi().then(answered).then(r => setUsers(r.users)));
      if (key === 'fleet') jobs.push(dronesJob(), getAdminUsersApi().then(answered).then(r => setUsers(r.users)));
      if (key === 'missions') jobs.push(dronesJob(), getAdminMissionsApi().then(answered).then(r => setMissions(r.missions)));
      if (key === 'deliveries') jobs.push(dronesJob(), getAdminDeliveriesApi().then(answered).then(r => setDeliveries(r.deliveries)));
      if (key === 'activity') jobs.push(dronesJob(), getAdminAuditApi(AUDIT_LIMIT).then(answered).then(r => setLogs(r.logs)));
      if (key === 'system') jobs.push(getAdminMonitorApi().then(answered).then(r => setMonitor(r.monitor)));
      await Promise.all(jobs);
      setLoaded(p => ({ ...p, [key]: true }));
      setLastOk(Date.now());
    } catch (e) {
      // A background refresh stays quiet: the header's "updated" time goes stale instead.
      if (!quiet) setMsg({ ok: false, text: `Could not load this section: ${e.message}` });
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) load(tab); }, [isAdmin, tab, load]);

  useEffect(() => {
    if (!isAdmin || !LIVE_TABS.has(tab)) return undefined;
    const id = setInterval(() => { if (!document.hidden) load(tab, true); }, 10000);
    return () => clearInterval(id);
  }, [isAdmin, tab, load]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // A success note clears itself; an error or a send in progress stays until replaced.
  useEffect(() => {
    if (!msg || !msg.ok || msg.busy) return undefined;
    const id = setTimeout(() => setMsg(null), 6000);
    return () => clearTimeout(id);
  }, [msg]);

  const go = (key) => {
    setTab(key); setMsg(null); setConfirm(null); setQ(''); setFilter('all'); setWho(''); setPickedIntent(null);
    try { sessionStorage.setItem(TAB_KEY, key); } catch { /* the section just is not remembered */ }
  };

  const liveCount = useMemo(() => INTENT_STATES.slice(0, 4).reduce((s, st) => s + (stats?.intentsByState?.[st.key] || 0), 0), [stats]);
  const deliveryTotal = useMemo(() => Object.values(stats?.deliveriesByStatus || {}).reduce((a, b) => a + b, 0), [stats]);
  const onlineCount = useMemo(() => drones.filter(d => isDroneOnline(d)).length, [drones, isDroneOnline]);
  const counts = {
    airspace: liveCount, members: stats?.totalUsers, fleet: stats?.totalDrones,
    missions: stats?.totalMissions, deliveries: deliveryTotal, activity: stats?.auditLast24h,
  };

  const needle = q.trim().toLowerCase();
  const has = (...fields) => !needle || fields.some(f => String(f || '').toLowerCase().includes(needle));
  const viewUsers = sortMembers(users).filter(u => memberMatches(u, filter) && has(u.name, u.email));
  const suspendedCount = users.filter(u => u.suspended).length;
  const ofWho = (x) => !who || x.owner?.name === who;
  const droneName = (id) => (drones.find(d => d.droneId === id) || {}).name || id;
  const viewDrones = sortFleet(drones, isDroneOnline).filter(d => {
    const online = isDroneOnline(d);
    if (filter === 'connected' && !online) return false;
    if (filter === 'disconnected' && online) return false;
    return ofWho(d) && has(d.name, d.droneId, d.owner?.name);
  });
  const viewMissions = [...missions]
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
    .filter(m => ofWho(m) && has(m.name, m.droneId, droneName(m.droneId), m.owner?.name));
  const viewDeliveries = sortDeliveries(deliveries).filter(d => (filter === 'all' || d.status === filter) && ofWho(d) && has(d.recipient, d.location, d.droneId, droneName(d.droneId), d.owner?.name));
  const viewLogs = logs.filter(l => auditMatches(l.action, filter) && has(l.action, AUDIT_TEXT[l.action], l.user?.name, l.detail, l.resourceId, droneName(l.resourceId)));

  const act = async (fn, after) => {
    setMsg(null);
    try {
      const r = await fn();
      setMsg({ ok: !!r.success, text: r.message || (r.success ? 'Done' : 'Request failed') });
      if (r.success) { setConfirm(null); after && after(); load(tab, true); }
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    }
  };

  // Set-password links go by email to the member's own inbox only; the admin never
  // sees one (a copied link is a password anyone holding it can set).
  const sendSetPasswordEmail = async (u, type = 'reset') => {
    setMsg({ ok: true, busy: true, text: `Sending the email to ${u.email}…` });
    setSendingTo(u.id);
    try {
      const r = await sendUserEmailApi(u.id, type);
      setMsg({ ok: !!r.success, text: r.message || (r.success ? `Email sent to ${u.email}` : 'Email could not be sent') });
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Email could not be sent' });
    } finally {
      setSendingTo(null);
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    if (addingRef.current) return;
    addingRef.current = true;
    setMsg(null);
    setAdding(true);
    try {
      const r = await inviteUserApi(newUser.name.trim(), newUser.email.trim(), newUser.role);
      // Account created but email failed is still a problem the admin must act on.
      setMsg({ ok: !!(r.success && r.emailSent), text: r.message || (r.success ? 'Member added' : 'Request failed') });
      if (r.success) {
        setNewUser({ name: '', email: '', role: 'operator' });
        load('members', true);
      }
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="adm">
        <div className="adm-denied">
          <div>
            <div className="adm-mark" style={{ margin: '0 auto', color: 'var(--a-red)', borderColor: 'var(--a-red)', boxShadow: 'none' }}><Icon d={I.lock} /></div>
            <h2>Admin access only</h2>
            <p>This console belongs to the airspace owner. Sign in with the admin account to use it.</p>
          </div>
        </div>
      </div>
    );
  }

  const svc = health?.services || {};
  const clock = new Date(now);
  const stale = LIVE_TABS.has(tab) && lastOk != null && now - lastOk > STALE_AFTER_MS;

  return (
    <div className={`adm${mob ? ' mob' : ''}`}>
      <header className="adm-head">
        <div className="adm-title">
          <img className="adm-logo" src={`${process.env.PUBLIC_URL}/BERAMLOGO.png`} alt="BERAM" />
          <div>
            <h1>Admin Console</h1>
            <p>{stats?.viewerIsSuperAdmin ? 'Super admin' : 'Admin'} · {currentUser?.email}</p>
          </div>
        </div>
        <div className="adm-clock">
          <div className="num">{clock.toISOString().slice(11, 19)}</div>
          <span>UTC, local {clock.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <div className="adm-health">
          <span className={`adm-pill ${svc.mongodb === 'up' ? 'up' : svc.mongodb ? 'down' : ''}`}><i />Database</span>
          <button type="button" className={`adm-refresh${stale ? ' stale' : ''}`} onClick={() => load(tab)} disabled={loading}
            title={stale ? 'The last automatic refresh did not get through. Refresh now.' : 'Refresh this section'}>
            <span className={loading ? 'spin' : ''}><Icon d={I.refresh} size={14} /></span>
            {lastOk ? `Updated ${ago(lastOk)}` : 'Refresh'}
          </button>
        </div>
      </header>

      <div className="adm-body">
        <nav className="adm-nav" aria-label="Admin sections">
          {NAV.map(n => n.group
            ? <div key={n.group} className="adm-nav-group">{n.group}</div>
            : (
              <button key={n.key} type="button" onClick={() => go(n.key)} aria-current={tab === n.key ? 'page' : undefined}>
                <Icon d={n.icon} />
                <span>{n.label}</span>
                {n.key === 'command'
                  ? liveCount > 0 && <span className="live-dot" title="Live reservations" />
                  : counts[n.key] != null && <span className="count num">{counts[n.key]}</span>}
              </button>
            ))}
        </nav>

        <main className="adm-main">
          <section>
            {msg && <div className="adm-toast-wrap"><div className={`adm-toast ${msg.ok ? 'ok' : 'err'}`} role="status">{msg.text}</div></div>}
            {!loaded[tab] ? (
              loading || !msg ? <div className="adm-loading">Loading…</div> : (
                <div className="adm-loading"><button type="button" className="adm-btn" onClick={() => load(tab)}>Try again</button></div>
              )
            ) : (
              <>
                {tab === 'command' && stats && <Command stats={stats} airspace={airspace} logs={logs} liveCount={liveCount} deliveryTotal={deliveryTotal} go={go} online={onlineCount} fleet={drones} database={svc.mongodb} />}

                {tab === 'airspace' && (
                  <Reservations
                    airspace={airspace}
                    fleet={drones}
                    now={now}
                    filter={filter}
                    onFilter={setFilter}
                    pickedId={pickedIntent}
                    onPick={setPickedIntent}
                    confirm={confirm}
                    onConfirm={setConfirm}
                    onRelease={(i) => act(() => releaseIntentApi(i.droneId), () => setPickedIntent(null))}
                  />
                )}

                {tab === 'members' && (
                  <>
                    <h2 className="adm-h2">Members</h2>
                    <p className="adm-lede">Only admins create accounts. Invites and password resets go by email to the member's own inbox; you never see the link or the password. {stats?.limits?.maxUsers ? `${users.length} of ${stats.limits.maxUsers} accounts used.` : ''}</p>
                    <form className="adm-panel adm-invite" onSubmit={addMember}>
                      <h3 className="adm-h3">Add member</h3>
                      <input className="adm-search" placeholder="Name (e.g. godown)" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} aria-label="New member name" required />
                      <input className="adm-search" type="email" placeholder="Email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} aria-label="New member email" required />
                      <select className="adm-search" value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))} aria-label="New member role">
                        <option value="operator">Operator</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button type="submit" className="adm-btn primary" disabled={adding}>{adding ? 'Sending…' : 'Send invite'}</button>
                    </form>
                    <div className="adm-tools">
                      <input className="adm-search" placeholder="Search name or email" value={q} onChange={e => setQ(e.target.value)} aria-label="Search members" />
                      <Seg label="Role" value={filter} onChange={setFilter} options={[
                        ['all', `All (${users.length})`],
                        ['admin', 'Admins'],
                        ['operator', 'Operators'],
                        ...(suspendedCount ? [['suspended', `Suspended (${suspendedCount})`]] : []),
                      ]} />
                    </div>
                    <div className="adm-panel flush adm-scroll">
                      <table className="adm-table adm-members">
                        <thead><tr><th>Member</th><th>Role</th><th className="r">Drones</th><th className="r hide-narrow">Missions</th><th className="hide-narrow">Joined</th><th className="r"><span className="sr">Actions</span></th></tr></thead>
                        <tbody>
                          {viewUsers.length === 0 && <tr><td colSpan={6} className="adm-empty">No members match that search.</td></tr>}
                          {viewUsers.map(u => {
                            const self = u.id === myId;
                            const asked = confirm?.id === u.id ? confirm : null;
                            return (
                              <tr key={u.id} className={u.suspended ? 'off' : undefined}>
                                <td className="who"><div className="adm-person">
                                  <AvatarDisplay avatarId={u.avatar} name={u.name} size={30} profilePhoto={u.profilePhoto} />
                                  <div><b>{u.name}{self && ' (you)'}</b><small>{u.email}</small></div>
                                </div></td>
                                <td className="role">
                                  {self || u.superAdmin
                                    ? <Tag color={u.role === 'admin' ? 'var(--a-cyan)' : 'var(--a-blue)'}>{u.superAdmin ? 'Super admin' : u.role === 'admin' ? 'Admin' : 'Operator'}</Tag>
                                    : (
                                      <select className="adm-search sm" value={asked?.action === 'role' ? asked.role : u.role} aria-label={`Role of ${u.name}`}
                                        onChange={e => setConfirm(e.target.value === u.role ? null : { id: u.id, action: 'role', role: e.target.value })}>
                                        <option value="operator">Operator</option>
                                        <option value="admin">Admin</option>
                                      </select>
                                    )}
                                  {u.suspended && <> <Tag color="var(--a-red)">Suspended</Tag></>}
                                </td>
                                <td className={`r num count${u.droneCount ? '' : ' zero'}`} data-label="Drones">{u.droneCount || 0}</td>
                                <td className={`r num count hide-narrow${u.missionCount ? '' : ' zero'}`} data-label="Missions">{u.missionCount || 0}</td>
                                <td className="joined hide-narrow" title={u.createdAt && new Date(u.createdAt).toLocaleString()}>{u.createdAt ? joinedText(u.createdAt) : '—'}</td>
                                <td className="r acts">
                                  <MemberActions
                                    member={u}
                                    self={self}
                                    targets={moveTargets(users, myId, u.id)}
                                    me={myId}
                                    asked={asked}
                                    sending={sendingTo === u.id}
                                    busy={sendingTo !== null}
                                    onAsk={setConfirm}
                                    onReset={() => sendSetPasswordEmail(u)}
                                    onSuspend={(suspended) => act(() => setUserSuspendedApi(u.id, suspended), () => setUsers(p => p.map(x => (x.id === u.id ? { ...x, suspended } : x))))}
                                    onRemove={(moveTo) => act(() => deleteUserApi(u.id, moveTo), () => setUsers(p => p.filter(x => x.id !== u.id)))}
                                    onRole={(role) => act(() => updateUserRoleApi(u.id, role), () => setUsers(p => p.map(x => (x.id === u.id ? { ...x, role } : x))))}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {tab === 'fleet' && (
                  <>
                    <h2 className="adm-h2">Fleet</h2>
                    <p className="adm-lede">Every drone registered by every operator. {onlineCount} of {drones.length} online.</p>
                    <div className="adm-tools">
                      <input className="adm-search" placeholder="Search name, ID or operator" value={q} onChange={e => setQ(e.target.value)} aria-label="Search fleet" />
                      <OperatorFilter list={drones} value={who} onChange={setWho} />
                      <Seg label="Link" value={filter} onChange={setFilter} options={[['all', `All (${drones.length})`], ['connected', `Online (${onlineCount})`], ['disconnected', `Offline (${Math.max(0, drones.length - onlineCount)})`]]} />
                    </div>
                    <div className="adm-panel flush adm-scroll">
                      <table className="adm-table adm-fleet">
                        <thead><tr><th>Drone</th><th>Status</th><th className="hide-mob hide-narrow">Link</th><th className="hide-mob">Now</th><th className="hide-mob">Operator</th><th className="hide-mob hide-narrow">Added</th><th className="r"><span className="sr">Actions</span></th></tr></thead>
                        <tbody>
                          {viewDrones.length === 0 && <tr><td colSpan={7} className="adm-empty">{drones.length ? 'No drones match.' : 'No drones yet. Operators add them from the Drones page.'}</td></tr>}
                          {viewDrones.map(d => {
                            const online = isDroneOnline(d);
                            const tel = (dronesTelemetry && dronesTelemetry[d.droneId]) || d.telemetry || {};
                            const battery = Number(tel.battery_remaining);
                            const hasBattery = online && tel.battery_remaining != null && tel.battery_remaining !== '' && Number.isFinite(battery) && battery >= 0;
                            const armed = online && !!tel.armed;
                            const targets = moveTargets(users, myId, d.owner?.id);
                            const moving = confirm?.id === d.id ? confirm : null;
                            const moveTo = moving?.to || targets[0]?.id;
                            return (
                              <React.Fragment key={d.id}>
                              <tr className={online ? undefined : 'dim'}>
                                <td className="drone"><div className="adm-person"><div><b>{d.name}</b><small className="id" title={d.droneId}>{d.droneId}</small><small className="show-mob">{d.owner?.name || 'No owner'}</small>{online && <small className="show-mob">{[tel.mode, hasBattery && `${Math.round(battery)}% battery`].filter(Boolean).join(' · ')}</small>}</div></div></td>
                                <td className="nowrap">
                                  <Tag color={online ? 'var(--a-green)' : 'var(--a-mute)'}>{online ? 'Online' : 'Offline'}</Tag>
                                  {online && tel.armed && <> <Tag color="var(--a-amber)">Armed</Tag></>}
                                </td>
                                <td className="hide-mob hide-narrow nowrap" title={d.connectionString || undefined}>{droneLink(d.connectionString, d.isSimulated)}{d.isSimulated && <small className="adm-line">Simulated</small>}</td>
                                <td className="hide-mob nowrap">
                                  {online ? (
                                    <>
                                      {tel.mode || '—'}
                                      {hasBattery && <small className="adm-line num" style={{ color: battery < 15 ? 'var(--a-red)' : battery < 25 ? 'var(--a-amber)' : undefined }}>{Math.round(battery)}% battery</small>}
                                    </>
                                  ) : '—'}
                                </td>
                                <td className="hide-mob">{d.owner?.name || <span className="muted">No owner</span>}</td>
                                <td className="hide-mob hide-narrow nowrap" title={d.createdAt && new Date(d.createdAt).toLocaleString()}>{d.createdAt ? joinedText(d.createdAt) : '—'}</td>
                                <td className="r acts">
                                  <button type="button" className="adm-btn" disabled={armed} title={armed ? 'A drone cannot change hands while armed' : 'Give this drone to another account'}
                                    onClick={() => setConfirm(moving ? null : { id: d.id, action: 'move' })}>Move…</button>
                                </td>
                              </tr>
                              {moving && (
                                <tr className="adm-res-ask">
                                  <td colSpan={7}>
                                    <div>
                                      <p>Its saved missions go with it and its link keeps working. Old flight logs stay with whoever flew them.</p>
                                      <TargetPicker label="Give to" targets={targets} me={myId} value={moveTo} onChange={(to) => setConfirm({ ...moving, to })} />
                                      <span className="adm-actions">
                                        <button type="button" className="adm-btn strong" disabled={!moveTo} onClick={() => act(() => moveDroneApi(d.id, moveTo))}>Move</button>
                                        <button type="button" className="adm-btn" onClick={() => setConfirm(null)}>Cancel</button>
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {tab === 'missions' && (
                  <>
                    <h2 className="adm-h2">Missions</h2>
                    <p className="adm-lede">Saved flight plans across all operators, last edited first. {missions.length} saved.</p>
                    <div className="adm-tools">
                      <input className="adm-search" placeholder="Search mission, drone or operator" value={q} onChange={e => setQ(e.target.value)} aria-label="Search missions" />
                      <OperatorFilter list={missions} value={who} onChange={setWho} />
                    </div>
                    <div className="adm-panel flush adm-scroll">
                      <table className="adm-table">
                        <thead><tr><th>Mission</th><th className="hide-mob">Drone</th><th className="r">Waypoints</th><th className="hide-mob">Operator</th><th className="hide-mob">Edited</th></tr></thead>
                        <tbody>
                          {viewMissions.length === 0 && <tr><td colSpan={5} className="adm-empty">{missions.length ? 'No missions match.' : 'No missions saved yet.'}</td></tr>}
                          {viewMissions.map(m => (
                            <tr key={m.id}>
                              <td className="ell wide" title={m.name}>{m.name}<small className="show-mob">{droneName(m.droneId)}</small></td>
                              <td className="hide-mob">{droneName(m.droneId)}</td>
                              <td className="r num">{m.waypointCount}</td>
                              <td className="hide-mob">{m.owner?.name || <span className="muted">No owner</span>}</td>
                              <td className="hide-mob nowrap" title={m.updatedAt && new Date(m.updatedAt).toLocaleString()}>{m.updatedAt ? joinedText(m.updatedAt) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {tab === 'deliveries' && (
                  <>
                    <h2 className="adm-h2">Deliveries</h2>
                    <p className="adm-lede">Parcel jobs from operators and customer drop links, what still needs something first. Operators approve customer requests from their Deliveries page.</p>
                    <div className="adm-tools">
                      <input className="adm-search" placeholder="Search recipient, site, drone or operator" value={q} onChange={e => setQ(e.target.value)} aria-label="Search deliveries" />
                      <OperatorFilter list={deliveries} value={who} onChange={setWho} />
                      <Seg label="Status" value={filter} onChange={setFilter} options={[
                        ['all', `All (${deliveries.length})`],
                        ...DELIVERY_STATUS_ORDER
                          .map(s => [s, deliveries.filter(j => j.status === s).length])
                          .filter(([, n]) => n > 0)
                          .map(([s, n]) => [s, `${deliveryStatusText(s)} (${n})`]),
                      ]} />
                    </div>
                    <div className="adm-panel flush adm-scroll">
                      <table className="adm-table">
                        <thead><tr><th>Recipient</th><th>Status</th><th className="hide-mob">Drop site</th><th className="r hide-mob">Weight</th><th className="hide-mob">Operator</th><th className="hide-mob">Created</th></tr></thead>
                        <tbody>
                          {viewDeliveries.length === 0 && <tr><td colSpan={6} className="adm-empty">{deliveries.length ? 'No deliveries match.' : 'No deliveries yet.'}</td></tr>}
                          {viewDeliveries.map(j => (
                            <tr key={j.id}>
                              <td><div className="adm-person"><div>
                                <b className={j.recipient ? undefined : 'muted'}>{j.recipient || 'No name'}</b>
                                <small>{[j.origin === 'public' ? 'Customer link' : 'Operator', j.droneId && droneName(j.droneId)].filter(Boolean).join(' · ')}</small>
                                <small className="show-mob">{j.location || 'No drop site'}</small>
                              </div></div></td>
                              <td className="nowrap"><Tag color={DELIVERY_COLOR[j.status] || 'var(--a-mute)'}>{deliveryStatusText(j.status)}</Tag></td>
                              <td className="hide-mob ell" title={j.location || undefined}>{j.location || '—'}</td>
                              <td className="r hide-mob num">{j.parcelWeightKg ? `${j.parcelWeightKg} kg` : '—'}</td>
                              <td className="hide-mob">{j.owner?.name || <span className="muted">No owner</span>}</td>
                              <td className="hide-mob nowrap" title={new Date(j.createdAt).toLocaleString()}>{joinedText(j.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {tab === 'activity' && (
                  <>
                    <h2 className="adm-h2">Audit trail</h2>
                    <p className="adm-lede">Who did what, newest first. Kept for 90 days{logs.length >= AUDIT_LIMIT ? `; this shows the latest ${AUDIT_LIMIT}` : ''}.</p>
                    <div className="adm-tools">
                      <input className="adm-search" placeholder="Search action, person or drone" value={q} onChange={e => setQ(e.target.value)} aria-label="Search audit trail" />
                      <Seg label="Kind" value={filter} onChange={setFilter} options={[['all', 'All'], ['serious', 'Serious'], ['flights', 'Flights'], ['drones', 'Drones'], ['members', 'Members'], ['settings', 'Settings']]} />
                    </div>
                    <div className="adm-panel flush adm-scroll">
                      <table className="adm-table adm-audit">
                        <thead><tr><th>Action</th><th className="hide-mob">By</th><th className="hide-mob">Target</th><th className="hide-mob">From</th><th className="r">Time</th></tr></thead>
                        <tbody>
                          {viewLogs.length === 0 && <tr><td colSpan={5} className="adm-empty">{logs.length ? 'Nothing matches.' : 'Nothing recorded yet.'}</td></tr>}
                          {viewLogs.map((l, i) => {
                            const day = auditDay(l.at, now);
                            const newDay = i === 0 || auditDay(viewLogs[i - 1].at, now) !== day;
                            const target = auditTarget(l, droneName);
                            const note = auditDetail(l.detail);
                            return (
                              <React.Fragment key={l.id}>
                                {newDay && <tr className="adm-day"><td colSpan={5}>{day}</td></tr>}
                                <tr>
                                  <td className="what">
                                    <div className="what-in">
                                      <i style={{ background: auditColor(l.action) }} />
                                      <div>
                                        <b>{AUDIT_TEXT[l.action] || l.action}</b>
                                        {note && <small className="note" title={note}>{note}</small>}
                                        <small className="show-mob">{[l.user?.name || 'System', target].filter(Boolean).join(' · ')}</small>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="hide-mob">{l.user?.name || <span className="muted">System</span>}</td>
                                  <td className="hide-mob" title={l.resourceId || undefined}>{target || '—'}</td>
                                  <td className="hide-mob id nowrap">{l.ip || '—'}</td>
                                  <td className="r nowrap" title={new Date(l.at).toLocaleString()}>{hhmm(l.at)}</td>
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {tab === 'system' && stats && (
                  <>
                    <h2 className="adm-h2">System</h2>
                    <p className="adm-lede">Backend health, API errors and plan limits. Refreshes on its own.</p>
                    <div className="adm-sys">
                      <div className="adm-panel">
                        <h3 className="adm-h3">Services</h3>
                        <dl className="adm-kv">
                          <dt>Backend</dt><dd><Tag color={svc.mongodb === 'up' ? 'var(--a-green)' : 'var(--a-red)'}>{svc.mongodb === 'up' ? 'healthy' : 'database down'}</Tag></dd>
                          <dt>Database</dt><dd>{svc.mongodb || '—'}</dd>
                          <dt title="Lab only. Cloud drones use MQTT, desktop links their own socket.">Python worker (lab only)</dt><dd>{svc.python === 'up' ? 'running' : 'not running'}</dd>
                          <dt>Build</dt><dd><span className="id">{health?.commit || 'local'}</span></dd>
                          <dt>Uptime</dt><dd className="num">{fmtUptime(health?.uptime)}</dd>
                        </dl>
                      </div>
                      <div className="adm-panel">
                        <h3 className="adm-h3">Plan limits</h3>
                        <Meter label="Members" value={stats.totalUsers} max={stats.limits?.maxUsers} color="var(--a-cyan)" />
                        <Meter label="Drones" value={stats.totalDrones} max={stats.limits?.maxDrones} color="var(--a-blue)" />
                      </div>
                      {monitor && <ApiHealth monitor={monitor} />}
                    </div>
                  </>
                )}
              </>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

const RELEASE_WARNING = {
  flying: { yes: 'Release anyway', tone: 'danger', note: 'This drone is armed and reporting. It keeps flying, and its airspace opens to other drones while it is still up.' },
  unknown: { yes: 'Release anyway', tone: 'danger', note: 'This drone is not reporting: it may have landed, or may still be up. Release only if you know it is down.' },
  ground: { yes: 'Release', tone: 'strong', note: 'The drone is on the ground. Its route stops being reserved.' },
};

function Reservations({ airspace, fleet, now, filter, onFilter, pickedId, onPick, confirm, onConfirm, onRelease }) {
  const nameOf = (id) => (fleet.find(d => d.droneId === id) || {}).name || id;
  const all = sortReservations(airspace.intents);
  const view = all.filter(i => filter === 'all' || i.state === filter);
  const picked = all.find(i => i.intentId === pickedId) || null;
  const stateFilters = INTENT_STATES
    .map(s => [s.key, s.label, all.filter(i => i.state === s.key).length])
    .filter(([, , n]) => n > 0)
    .map(([key, label, n]) => [key, `${label} (${n})`]);

  return (
    <>
      <h2 className="adm-h2">Reservations</h2>
      <p className="adm-lede">Every drone books its route and time before takeoff, so no two share the same airspace. A booking ends by itself when the flight does. Release one by hand only when it got stuck.</p>
      {all.length > 0 && (
        <div className="adm-tools">
          <Seg label="State" value={filter} onChange={onFilter} options={[['all', `All (${all.length})`], ...stateFilters]} />
        </div>
      )}
      <div className="adm-res">
        <div className="adm-panel flush">
          <table className="adm-table adm-res-table">
            <thead><tr><th>Drone</th><th>State</th><th>Time</th><th className="r"><span className="sr">Release</span></th></tr></thead>
            <tbody>
              {view.length === 0 && <tr><td colSpan={4} className="adm-empty">{all.length ? 'None in this state.' : 'No live reservations. The airspace is open.'}</td></tr>}
              {view.map(i => {
                const st = STATE_BY_KEY[i.state] || STATE_BY_KEY.Accepted;
                const clock = reservationClock(i, now);
                const isPicked = pickedId === i.intentId;
                const warning = confirm?.id === i.intentId ? RELEASE_WARNING[releaseRisk(i, airspace.drones)] : null;
                return (
                  <React.Fragment key={i.intentId}>
                  <tr className={isPicked ? 'picked' : undefined} onClick={() => onPick(isPicked ? null : i.intentId)}>
                    <td className="who">
                      <div className="adm-person"><div>
                        <b><button type="button" className="adm-rowlink" aria-pressed={isPicked} onClick={e => { e.stopPropagation(); onPick(isPicked ? null : i.intentId); }}>{nameOf(i.droneId)}</button></b>
                        <small>{i.owner?.name || 'Unknown operator'}</small>
                      </div></div>
                    </td>
                    <td className="state">
                      <Tag color={st.color}>{st.label}</Tag>
                      {i.priority && <> <Tag color="var(--a-red)">Priority</Tag></>}
                      {i.stateReason && <small className="why">{i.stateReason}</small>}
                    </td>
                    <td className="time">
                      <b className={clock.overdue ? 'late' : undefined}>{clock.text}</b>
                      <small className="num">{hhmm(i.timeStart)} to {hhmm(i.timeEnd)}</small>
                    </td>
                    <td className="r acts" onClick={e => e.stopPropagation()}>
                      <button type="button" className="adm-btn" aria-expanded={!!warning} onClick={() => onConfirm(warning ? null : { id: i.intentId })}>Release…</button>
                    </td>
                  </tr>
                  {warning && (
                    <tr className={`adm-res-ask ${warning.tone}`}>
                      <td colSpan={4}>
                        <div>
                          <p>{warning.note}</p>
                          <span className="adm-actions">
                            <button type="button" className={`adm-btn ${warning.tone}`} onClick={() => onRelease(i)}>{warning.yes}</button>
                            <button type="button" className="adm-btn" onClick={() => onConfirm(null)}>Keep</button>
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="adm-panel flush adm-res-scope">
          <div className="adm-scope-head">
            <h3 className="adm-h3" style={{ margin: 0 }}>{picked ? nameOf(picked.droneId) : 'Airspace scope'}</h3>
            <small>{picked ? <button type="button" className="adm-rowlink" onClick={() => onPick(null)}>Show all</button> : 'Pick a row to see its route'}</small>
          </div>
          <Scope intents={airspace.intents} drones={airspace.drones} selected={picked} />
          <ScopeLegend />
        </div>
      </div>
    </>
  );
}

// Every member's row has the same two buttons in the same two places, so the column
// reads as a straight line. A step that cannot be undone asks first, in place, and
// says what it does.
function MemberActions({ member: u, self, me, targets, asked, sending, busy, onAsk, onReset, onSuspend, onRemove, onRole }) {
  const ask = (action, more) => onAsk({ id: u.id, action, ...more });
  const cancel = () => onAsk(null);
  const drones = u.droneCount || 0;

  const question = {
    suspend: {
      yes: 'Suspend', run: () => onSuspend(true), danger: true,
      note: 'Blocks login and ends their sessions now. Their drones and logs stay.',
    },
    remove: {
      yes: 'Remove', run: () => onRemove(asked.moveTo), danger: true, blocked: drones > 0 && !asked?.moveTo,
      note: `${drones ? 'Drones keep their missions and their link. ' : ''}Deliveries not flown yet are cancelled and customer links stop. Flight logs stay under this name. The email can be invited again.`,
    },
    role: asked?.role === 'admin'
      ? { yes: 'Make admin', run: () => onRole('admin'), note: 'Admins see every operator\'s data and this console.' }
      : { yes: 'Make operator', run: () => onRole('operator'), note: 'They lose this console and see only their own drones.' },
  }[asked?.action];

  if (question) {
    return (
      <div className="adm-ask">
        {asked.action === 'remove' && drones > 0 && (
          <TargetPicker label={`Give ${drones} drone${drones === 1 ? '' : 's'} to`} targets={targets} me={me} value={asked.moveTo} onChange={(moveTo) => ask('remove', { moveTo })} />
        )}
        <div className="adm-row-actions">
          <button type="button" className={`adm-btn${question.danger ? ' danger' : ' strong'}`} disabled={question.blocked} onClick={question.run}>{question.yes}</button>
          <button type="button" className="adm-btn" onClick={cancel}>Cancel</button>
        </div>
        <small>{question.note}</small>
      </div>
    );
  }

  if (u.suspended) {
    return (
      <div className="adm-row-actions">
        <button type="button" className="adm-btn good" onClick={() => onSuspend(false)} title="Let this member log in again">Reactivate</button>
        <button type="button" className="adm-btn danger" onClick={() => ask('remove', { moveTo: targets[0]?.id })} title="Close this account for good. Nothing is left without an owner.">Remove…</button>
      </div>
    );
  }

  const protectedAccount = self || u.superAdmin || u.role === 'admin';
  return (
    <div className="adm-row-actions">
      <button type="button" className="adm-btn" onClick={onReset} disabled={busy} title={`Email a password reset link to ${u.email}`}>
        <Icon d={I.mail} size={13} />{sending ? 'Sending…' : 'Send reset'}
      </button>
      <button type="button" className="adm-btn" disabled={protectedAccount} onClick={() => ask('suspend')}
        title={protectedAccount ? (self ? 'You cannot suspend your own account' : 'Admins cannot be suspended. Make them an operator first.') : 'Block login. It can be undone.'}>
        Suspend…
      </button>
    </div>
  );
}

// Who gets a drone. You are first, and the default: an admin can hold it until a new
// godown takes it.
function TargetPicker({ label, targets, me, value, onChange }) {
  return (
    <label className="adm-target">
      <span>{label}</span>
      <select className="adm-search sm" value={value || ''} onChange={e => onChange(e.target.value)}>
        {targets.map(t => <option key={t.id} value={t.id}>{t.name}{t.id === me ? ' (you)' : t.role === 'admin' ? ' (admin)' : ''}</option>)}
      </select>
    </label>
  );
}

function ScopeLegend() {
  return (
    <div className="adm-scope-legend">
      {INTENT_STATES.slice(0, 4).map(s => <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>)}
      <span><i style={{ background: 'var(--a-green)', width: 7, height: 7, borderRadius: '50%' }} />Armed drone</span>
      <span><i style={{ background: 'var(--a-cyan)', width: 7, height: 7, borderRadius: '50%' }} />Online, on ground</span>
    </div>
  );
}

function Command({ stats, airspace, logs, liveCount, deliveryTotal, go, online, fleet, database }) {
  const byState = stats.intentsByState || {};
  const dl = stats.deliveriesByStatus || {};
  const total = stats.totalDrones || 0;
  const armed = airspace.drones.filter(d => d.armed).length;
  const onGround = Math.max(0, online - armed);
  const attention = attentionItems({ intents: airspace.intents, fleet, stats, database });
  const n = attention.length;

  const figures = [
    { go: 'members', value: stats.totalUsers, label: 'Members', note: memberSummary(stats) },
    { go: 'fleet', value: total, label: 'Drones', note: `${online} online now` },
    { go: 'missions', value: stats.totalMissions, label: 'Missions', note: 'saved plans' },
    { go: 'deliveries', value: deliveryTotal, label: 'Deliveries', note: `${dl.in_flight || 0} in flight, ${dl.requested || 0} waiting approval` },
    { go: 'activity', value: stats.auditLast24h ?? 0, label: 'Audit events', note: 'in the last 24 hours' },
  ];

  return (
    <>
      <h2 className="adm-h2">{n ? `${n} ${n === 1 ? 'thing needs' : 'things need'} attention` : 'All clear'}</h2>
      <p className="adm-lede">{liveCount} live reservation{liveCount === 1 ? '' : 's'}, {armed} armed, {online} of {total} drones online.</p>

      {n > 0 && (
        <ul className="adm-attention" aria-label="Needs attention">
          {attention.map(a => (
            <li key={a.key}>
              <button type="button" className={a.level} onClick={() => go(a.go)}>
                <i />
                <span className="txt"><b>{a.title}</b><small>{a.detail}</small></span>
                <span className="open">Open</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="adm-ledger">
        {figures.map(f => (
          <button key={f.go} type="button" onClick={() => go(f.go)} title={`Open ${f.label}`}>
            <span className="num">{f.value}</span><span>{f.label}</span><small>{f.note}</small>
          </button>
        ))}
      </div>

      <div className="adm-command">
        <div className="adm-panel flush">
          <div className="adm-scope-head"><h3 className="adm-h3" style={{ margin: 0 }}>Airspace scope</h3><small>{airspace.drones.length} positioned, {airspace.intents.length} routes</small></div>
          <Scope intents={airspace.intents} drones={airspace.drones} />
          <ScopeLegend />
        </div>
        <div className="adm-side">
          <div className="adm-panel">
            <h3 className="adm-h3">Reservations by state</h3>
            <div className="adm-states">
              {INTENT_STATES.map(s => {
                const count = byState[s.key] || 0;
                return (
                  <div key={s.key} className={`adm-state${count ? '' : ' none'}`}>
                    <i style={{ background: s.color }} />
                    <div><b>{s.label}</b><small>{s.note}</small></div>
                    <span className="num" style={{ color: count && s.key !== 'Ended' ? s.color : 'var(--a-mute)' }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="adm-panel">
            <h3 className="adm-h3">Fleet readiness</h3>
            <div className="adm-meter-bar split" style={{ height: 10 }}>
              <div style={{ width: `${total ? (armed / total) * 100 : 0}%`, background: 'var(--a-green)' }} title="Armed" />
              <div style={{ width: `${total ? (onGround / total) * 100 : 0}%`, background: 'var(--a-cyan)' }} title="Online, on the ground" />
            </div>
            <dl className="adm-ready">
              <div><dt><i style={{ background: 'var(--a-green)' }} />Armed</dt><dd className="num">{armed}</dd></div>
              <div><dt><i style={{ background: 'var(--a-cyan)' }} />On ground</dt><dd className="num">{onGround}</dd></div>
              <div><dt><i style={{ background: 'var(--a-line-hi)' }} />Offline</dt><dd className="num">{Math.max(0, total - online)}</dd></div>
            </dl>
          </div>
        </div>
      </div>

      <div className="adm-panel adm-latest">
        <div className="adm-panel-head">
          <h3 className="adm-h3" style={{ margin: 0 }}>Latest activity</h3>
          <button type="button" className="adm-btn" onClick={() => go('activity')}>Open audit trail</button>
        </div>
        <AuditFeed logs={logs} nameOf={(id) => (fleet.find(d => d.droneId === id) || {}).name || id} />
      </div>
    </>
  );
}

export default AdminPage;
